'use client';

import { useEffect, useState } from 'react';

import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import resourcesToBackend from 'i18next-resources-to-backend';
import { useParams } from 'next/navigation';
import { useCookies } from 'react-cookie';
import { initReactI18next, useTranslation as useTranslationOrg } from 'react-i18next';

import { useEnv } from '@lib/components/envProvider';
import { getOptions, LANGUAGES_AVAILABLE, LANGUAGES_COOKIE_NAME, Locale } from '@lib/services/i18n';
import { PagePropsParams } from '@lib/services/page';

const isServerSide = typeof window === 'undefined';

i18next
  .use(initReactI18next)
  .use(LanguageDetector)
  .use(
    resourcesToBackend(
      (language: string, namespace: string) => import(`./locales/${language}/${namespace}.json`)
    )
  )
  // .use(LocizeBackend) // locize backend could be used on client side, but prefer to keep it in sync with server side
  .init({
    ...getOptions(),
    lng: undefined, // let detect the language on client side
    detection: {
      order: ['path', 'htmlTag', 'cookie', 'navigator']
    },
    preload: isServerSide ? LANGUAGES_AVAILABLE : []
  });

export function useTranslation(ns: string = 'default', options: any = {}, locale?: Locale) {
  const params = useParams() as PagePropsParams;
  const lng = locale || params?.lng;
  const { languages } = useEnv();
  const [cookies, setCookie, removeCookie] = useCookies([LANGUAGES_COOKIE_NAME]);
  const ret = useTranslationOrg(ns, options);
  const { i18n } = ret;

  if (isServerSide && lng && i18n.resolvedLanguage !== lng) {
    i18n.changeLanguage(lng);
  } else {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [activeLng, setActiveLng] = useState(i18n.resolvedLanguage);

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      if (activeLng === i18n.resolvedLanguage) return;
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setActiveLng(i18n.resolvedLanguage);
    }, [activeLng, i18n.resolvedLanguage]);

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      if (!lng || i18n.resolvedLanguage === lng) return;
      i18n.changeLanguage(lng);
    }, [lng, i18n]);

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      if (cookies.i18next === lng) {
        return;
      }
      if (!languages || languages.length > 1) {
        setCookie(LANGUAGES_COOKIE_NAME, lng, { path: '/' });
      } else {
        removeCookie(LANGUAGES_COOKIE_NAME, { path: '/' });
      }
    }, [lng, cookies.i18next, setCookie, removeCookie, languages]);
  }

  return ret;

  return {} as any;
}
